import { useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Heading,
  HStack,
  Icon,
  Spinner,
  Stack,
  Table,
  Text,
} from '@chakra-ui/react';
import { LuArchive, LuArchiveRestore, LuLink, LuPlus } from 'react-icons/lu';
import { useNavigate, useParams } from 'react-router-dom';
import {
  archiveClient,
  getClient,
  linkClientToProject,
  listSellers,
  unarchiveClient,
  updateClient,
  type ClientDetail,
} from '@/lib/api';
import { useProject } from '@/lib/ProjectContext';
import { useMe } from '@/lib/MeContext';
import { toaster } from '@/components/ui/toaster';
import { ClientForm, type ClientFormValues } from './ClientForm';
import { DocumentsPanel } from '@/components/documents/DocumentsPanel';
import { ContractSummaryPanel } from '@/components/contract/ContractSummaryPanel';

export function ClientEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { activeProject } = useProject();
  const { me } = useMe();
  const [client, setClient] = useState<ClientDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);
  const [sellers, setSellers] = useState<{ id: string; fullName: string }[]>([]);

  async function refresh() {
    if (!id) return;
    setLoading(true);
    setLoadError(null);
    try {
      setClient(await getClient(id));
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'No se pudo cargar');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, [id]);

  useEffect(() => {
    listSellers({ status: 'active' })
      .then((rows) => setSellers(rows.map((s) => ({ id: s.id, fullName: s.fullName }))))
      .catch(() => setSellers([]));
  }, []);

  async function handleSubmit(values: ClientFormValues) {
    if (!id) return;
    setFormError(null);
    setSubmitting(true);
    try {
      await updateClient(id, {
        fullName: values.fullName.trim(),
        email: values.email.trim() || null,
        phone: values.phone.trim() || null,
        address: values.address.trim() || null,
        notes: values.notes.trim() || null,
        accountOwnerSellerId: values.accountOwnerSellerId || null,
      });
      toaster.create({ title: 'Cliente actualizado', type: 'success' });
      navigate('/clientes');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'No se pudo guardar';
      setFormError(msg);
      toaster.create({ title: 'No se pudo guardar', description: msg, type: 'error' });
    } finally {
      setSubmitting(false);
    }
  }

  async function handleArchiveToggle() {
    if (!client) return;
    setBusy(true);
    try {
      if (client.status === 'active') {
        await archiveClient(client.id);
        toaster.create({ title: 'Cliente archivado', type: 'success' });
      } else {
        await unarchiveClient(client.id);
        toaster.create({ title: 'Cliente restaurado', type: 'success' });
      }
      navigate('/clientes');
    } catch (err) {
      toaster.create({
        title: 'Operación fallida',
        description: err instanceof Error ? err.message : '',
        type: 'error',
      });
    } finally {
      setBusy(false);
    }
  }

  async function handleLinkToActive() {
    if (!client || !activeProject) return;
    setLinkBusy(true);
    try {
      const result = await linkClientToProject(client.id, activeProject.id);
      if ('conflict' in result) {
        toaster.create({
          title: 'Ya estaba vinculado',
          description: result.conflict.code
            ? `Código existente: ${result.conflict.code}`
            : result.conflict.error,
          type: 'info',
        });
      } else {
        toaster.create({
          title: 'Cliente vinculado al proyecto',
          description: `${result.code} · ${activeProject.name}`,
          type: 'success',
        });
        await refresh();
      }
    } catch (err) {
      toaster.create({
        title: 'No se pudo vincular',
        description: err instanceof Error ? err.message : '',
        type: 'error',
      });
    } finally {
      setLinkBusy(false);
    }
  }

  if (loading) {
    return (
      <Box px={{ base: '4', md: '8' }} py={{ base: '6', md: '8' }}>
        <HStack gap="3" color="fg.muted">
          <Spinner size="sm" />
          <Text fontSize="sm">Cargando…</Text>
        </HStack>
      </Box>
    );
  }

  if (loadError || !client) {
    return (
      <Box px={{ base: '4', md: '8' }} py={{ base: '6', md: '8' }}>
        <Stack gap="4" maxW="3xl">
          <Text color="red.600" fontSize="sm">
            {loadError ?? 'Cliente no encontrado'}
          </Text>
          <Box>
            <Button variant="outline" onClick={() => navigate('/clientes')}>
              Volver
            </Button>
          </Box>
        </Stack>
      </Box>
    );
  }

  const initial: ClientFormValues = {
    fullName: client.fullName,
    documentType: client.documentType,
    documentNumber: client.documentNumber,
    email: client.email ?? '',
    phone: client.phone ?? '',
    address: client.address ?? '',
    notes: client.notes ?? '',
    accountOwnerSellerId: client.accountOwnerSellerId ?? '',
  };

  const linkedProjectIds = new Set(client.memberships.map((m) => m.projectId));
  const canLinkActive =
    activeProject && !linkedProjectIds.has(activeProject.id) && client.status === 'active';
  const isAdmin = me?.role === 'admin';

  return (
    <Box px={{ base: '4', md: '8' }} py={{ base: '6', md: '8' }}>
      <Stack gap="6" maxW="3xl">
        <Stack gap="1">
          <Text
            fontSize="xs"
            color="fg.muted"
            textTransform="uppercase"
            letterSpacing="0.06em"
            fontWeight="700"
          >
            Clientes
          </Text>
          <HStack gap="3" align="center" wrap="wrap">
            <Heading size={{ base: 'xl', md: '2xl' }} letterSpacing="-0.02em">
              {client.fullName}
            </Heading>
            <Badge colorPalette={client.status === 'active' ? 'green' : 'gray'}>
              {client.status === 'active' ? 'Activo' : 'Archivado'}
            </Badge>
          </HStack>
          <Text color="fg.muted">
            {client.memberships.length === 0
              ? 'Sin proyectos vinculados'
              : client.memberships.map((m) => m.code).join(' · ')}
          </Text>
        </Stack>

        <ClientForm
          initial={initial}
          lockIdentity
          submitLabel="Guardar cambios"
          submitting={submitting}
          error={formError}
          sellers={sellers}
          onSubmit={handleSubmit}
          onCancel={() => navigate('/clientes')}
        />

        <Box
          bg="bg.surface"
          borderWidth="1px"
          borderColor="border"
          rounded="lg"
          p={{ base: '4', md: '6' }}
        >
          <Stack
            direction={{ base: 'column', md: 'row' }}
            justify="space-between"
            align={{ base: 'stretch', md: 'flex-start' }}
            gap="3"
            mb="4"
          >
            <Stack gap="0.5">
              <Heading size="sm">Proyectos vinculados</Heading>
              <Text color="fg.muted" fontSize="sm">
                Cada vínculo genera un código único por proyecto.
              </Text>
            </Stack>
            {canLinkActive && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleLinkToActive}
                loading={linkBusy}
                w={{ base: 'full', md: 'auto' }}
              >
                <Icon as={LuPlus} /> Vincular a {activeProject.code}
              </Button>
            )}
          </Stack>
          {client.memberships.length === 0 ? (
            <Text color="fg.muted" fontSize="sm">
              Aún no está vinculado a ningún proyecto.
            </Text>
          ) : (
            <>
              <Stack gap="2" display={{ base: 'flex', md: 'none' }}>
                {client.memberships.map((m) => (
                  <Box key={m.id} borderWidth="1px" borderColor="border" rounded="md" p="3">
                    <HStack justify="space-between" gap="3">
                      <Stack gap="0.5" minW={0}>
                        <Text fontWeight="600">{m.code}</Text>
                        <Text fontSize="sm" color="fg.muted" truncate>
                          {m.projectName}
                        </Text>
                      </Stack>
                      <Icon as={LuLink} color="fg.muted" flexShrink={0} />
                    </HStack>
                  </Box>
                ))}
              </Stack>
              <Box display={{ base: 'none', md: 'block' }}>
                <Table.Root size="sm">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader>Código</Table.ColumnHeader>
                      <Table.ColumnHeader>Proyecto</Table.ColumnHeader>
                      <Table.ColumnHeader>Vinculado</Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {client.memberships.map((m) => (
                      <Table.Row key={m.id}>
                        <Table.Cell fontWeight="600">{m.code}</Table.Cell>
                        <Table.Cell>{m.projectName}</Table.Cell>
                        <Table.Cell color="fg.muted">
                          {new Date(m.assignedAt).toLocaleDateString('es-GT')}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              </Box>
            </>
          )}
        </Box>

        {activeProject && (
          <Box
            bg="bg.surface"
            borderWidth="1px"
            borderColor="border"
            rounded="lg"
            p={{ base: '4', md: '6' }}
          >
            <Heading size="sm" mb="1">
              Contrato
            </Heading>
            <Text color="fg.muted" fontSize="sm" mb="4">
              Contratos de este cliente en {activeProject.code} · {activeProject.name}.
            </Text>
            <ContractSummaryPanel clientId={client.id} projectId={activeProject.id} />
          </Box>
        )}

        <Box
          bg="bg.surface"
          borderWidth="1px"
          borderColor="border"
          rounded="lg"
          p={{ base: '4', md: '6' }}
        >
          <Heading size="sm" mb="3">
            Documentos
          </Heading>
          <DocumentsPanel parent={{ type: 'client', id: client.id }} canDelete={isAdmin} />
        </Box>

        {isAdmin && (
          <Box
            bg="bg.surface"
            borderWidth="1px"
            borderColor="border"
            rounded="lg"
            p={{ base: '4', md: '6' }}
          >
            <Heading size="sm" mb="2">
              {client.status === 'active' ? 'Archivar cliente' : 'Restaurar cliente'}
            </Heading>
            <Text color="fg.muted" fontSize="sm" mb="4">
              {client.status === 'active'
                ? 'El cliente deja de aparecer en las listas activas. Sus contratos y pagos se conservan y puede restaurarse en cualquier momento.'
                : 'El cliente vuelve a estar disponible en las listas activas.'}
            </Text>
            <Button
              variant="outline"
              colorPalette={client.status === 'active' ? 'gray' : 'green'}
              onClick={handleArchiveToggle}
              loading={busy}
              w={{ base: 'full', md: 'auto' }}
            >
              <Icon as={client.status === 'active' ? LuArchive : LuArchiveRestore} />
              {client.status === 'active' ? 'Archivar' : 'Restaurar'}
            </Button>
          </Box>
        )}
      </Stack>
    </Box>
  );
}
